// src/components/PackageStore.js - Fixed to respect existing package purchases
import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";
import {
  ArrowBack,
  ShoppingCart,
  Refresh,
  AccountCircle,
  Star,
  CheckCircle,
  LocalOffer,
  EmojiEvents,
  Info,
  Block,
  Close,
  Add,
  Remove,
  Warning,
} from "@mui/icons-material";

// Import the same components used in HomePage
import NavBar from "./NavBar";
import CartDrawer from "./CartDrawer";
import UserMenu from "./UserMenu";
import Footer from "./Footer";
import FloatingCartButton from "./FloatingCartButton";
import Notifications from "./Notifications";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import {
  AUTO_FILL_UPGRADE_AMOUNT,
  CURRENT_ACTIVATION_FEE,
  LEGACY_ACTIVATION_FEE,
} from "../config/membershipConfig";
import {
  buildAutoFillUpgradePackage,
  isEligibleForAutoFillUpgrade,
  isPublicMembershipPackageListing,
} from "../utils/membershipUpgrade";
import MembershipVoucherRedeem from "./MembershipVoucherRedeem";

const PackageStore = ({
  user,
  userProfile,
  packages,
  packagesLoading,
  onBack,
  onPackageSelect,
  onRefreshProfile,
  // Add these props for consistency with HomePage
  cart = { packages: [], items: [] },
  removeFromCart = () => {},
  updateCartQuantity = () => {},
  canAddMoreToCart = () => true,
  onSignOut = () => {},
  onAdminPanel = () => {},
  isAdmin = false,
}) => {
  // States for UI consistency with HomePage
  const [anchorEl, setAnchorEl] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories] = useState(["All"]);

  // Sub-item selection states
  const [subItemSelectionOpen, setSubItemSelectionOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSubItems, setSelectedSubItems] = useState({});
  const [subItemQuantities, setSubItemQuantities] = useState({});
  
  // Sub-item details preview states
  const [subItemDetailsOpen, setSubItemDetailsOpen] = useState(false);
  const [previewPackage, setPreviewPackage] = useState(null);

  // HomePage styling - exact same styles

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // HomePage functions for UI consistency
  const handleMenu = (event) => {
    // Since we're already on PackageStore page, clicking profile icon should go back to home
    if (onBack) {
      onBack();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    onSignOut();
    handleClose();
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "" });
  };

  const getCartItemCount = () => {
    return (
      cart.packages.reduce((sum, p) => sum + p.quantity, 0) +
      cart.items.reduce((sum, i) => sum + i.quantity, 0)
    );
  };

  const getCartTotal = () => {
    const packageTotal = cart.packages.reduce(
      (sum, pkg) => {
        // For packages with sub-items, calculate GST properly
        if (pkg.hasSubItems && pkg.selectedSubItems && Object.keys(pkg.selectedSubItems).length > 0) {
          let subItemsTotal = 0;
          Object.entries(pkg.selectedSubItems).forEach(([subItemId, isSelected]) => {
            if (isSelected) {
              const subItem = pkg.subItems?.find(item => item.id.toString() === subItemId);
              if (subItem) {
                const quantity = pkg.subItemQuantities?.[subItemId] || 1;
                const gstInfo = calculateSubItemGST(subItem);
                subItemsTotal += gstInfo.totalWithGST * quantity;
              }
            }
          });
          return sum + (subItemsTotal * pkg.quantity);
        } 
        // For packages with sub-items that use selectedSubItemsList (array format)
        else if (pkg.isSubItemPackage && pkg.selectedSubItemsList && pkg.selectedSubItemsList.length > 0) {
          let subItemsTotal = 0;
          pkg.selectedSubItemsList.forEach((subItem) => {
            const quantity = subItem.selectedQuantity || 1;
            const gstInfo = calculateSubItemGST(subItem);
            subItemsTotal += gstInfo.totalWithGST * quantity;
          });
          return sum + (subItemsTotal * pkg.quantity);
        }
        // Regular packages WITHOUT sub-items - NO GST, use price as-is
        else {
          return sum + pkg.price * pkg.quantity;
        }
      },
      0
    );
    const itemTotal = cart.items.reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    return packageTotal + itemTotal;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  // GST calculation function for sub-items - GST is calculated on BASE PRICE ONLY
  const calculateSubItemGST = (subItem) => {
    const packagePrice = subItem.itemPrice || 0; // This is the selling/package price (base + profit)
    const basePrice = subItem.basePrice || 0; // Use basePrice for GST calculation
    const profitMargin = subItem.profitMargin || 0; // Profit portion
    const gstPercentage = (subItem.gstPercentage !== undefined && subItem.gstPercentage !== null && subItem.gstPercentage !== '') ? Number(subItem.gstPercentage) : 18;
    
    // GST should only be calculated on the base price, not on profit margin
    const gstAmount = basePrice > 0 ? (basePrice * gstPercentage) / 100 : 0;
    
    // Final price = base price + profit + GST (on base price only)
    const totalWithGST = basePrice + profitMargin + gstAmount;
    
    return {
      basePrice: basePrice,
      profitMargin: profitMargin,
      packagePrice: packagePrice,
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      totalWithGST: totalWithGST
    };
  };

  // Discount calculation functions for sub-items
  // Savings = MRP - (package price + GST)
  const calculateSubItemDiscount = (subItem) => {
    const packagePrice = subItem.itemPrice || 0;
    const sellingPrice = subItem.sellingPrice || 0;
    const compareAtPrice = subItem.compareAtPrice || 0;
    
    // Calculate GST for this item
    const basePrice = subItem.basePrice || packagePrice;
    const gstPercentage = (subItem.gstPercentage !== undefined && subItem.gstPercentage !== null && subItem.gstPercentage !== '') ? Number(subItem.gstPercentage) : 18;
    const gstAmount = basePrice > 0 ? (basePrice * gstPercentage) / 100 : 0;
    const packagePriceWithGST = packagePrice + gstAmount;
    
    const discounts = [];
    
    // Discount vs Selling Price (including GST)
    if (sellingPrice > 0 && packagePriceWithGST < sellingPrice) {
      const savingAmount = sellingPrice - packagePriceWithGST;
      const savingPercentage = ((savingAmount / sellingPrice) * 100);
      discounts.push({
        type: 'selling',
        originalPrice: sellingPrice,
        packagePrice: packagePriceWithGST,
        savingAmount: savingAmount,
        savingPercentage: savingPercentage,
        label: 'vs Individual Price'
      });
    }
    
    // Discount vs Compare At Price (MRP) - Savings = MRP - (package price + GST)
    if (compareAtPrice > 0 && packagePriceWithGST < compareAtPrice) {
      const savingAmount = compareAtPrice - packagePriceWithGST;
      const savingPercentage = ((savingAmount / compareAtPrice) * 100);
      discounts.push({
        type: 'mrp',
        originalPrice: compareAtPrice,
        packagePrice: packagePriceWithGST,
        savingAmount: savingAmount,
        savingPercentage: savingPercentage,
        label: 'vs MRP'
      });
    }
    
    // Return the best discount (highest percentage)
    if (discounts.length === 0) return null;
    return discounts.reduce((best, current) => 
      current.savingPercentage > best.savingPercentage ? current : best
    );
  };

  const getPackageFeatureLimit = (pkg) => {
    // Show more features for packages since they're the main focus
    return pkg.features ? Math.min(pkg.features.length, 6) : 0;
  };

  const getPackageBadge = (pkg) => {
    // Add special badges for packages
    if (pkg.hasSubItems) return { label: "Flexible", color: "secondary" };
    if (pkg.price >= 5000) return { label: "Premium", color: "secondary" };
    if (pkg.price >= 2000) return { label: "Popular", color: "primary" };
    if (pkg.price < 1000) return { label: "Starter", color: "success" };
    return { label: "Standard", color: "info" };
  };

  // ✅ CHECK IF USER ALREADY HAS A PACKAGE
  const hasPackage = userProfile?.hasPurchasedPackage === true;
  const showAutoFillUpgrade = isEligibleForAutoFillUpgrade(userProfile);
  const storePackages = (packages || []).filter(isPublicMembershipPackageListing);

  // Debug logging
  console.log("=== PACKAGE STORE DEBUG ===");
  console.log("userProfile:", userProfile);
  console.log("hasPurchasedPackage:", userProfile?.hasPurchasedPackage);
  console.log("hasPackage (computed):", hasPackage);
  console.log("========================");

  // Sub-item selection helper functions
  // Calculate sub-item total WITHOUT GST - GST will be calculated separately in checkout
  const calculateSubItemTotal = () => {
    let total = 0;
    Object.entries(selectedSubItems).forEach(([subItemId, isSelected]) => {
      if (isSelected && selectedPackage) {
        const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
        if (subItem) {
          const quantity = subItemQuantities[subItemId] || 1;
          // Use selling price (base + profit) WITHOUT GST
          const itemPrice = subItem.sellingPrice > 0 ? subItem.sellingPrice : (subItem.basePrice + subItem.profitMargin);
          total += itemPrice * quantity;
        }
      }
    });
    return total;
  };

  // Calculate sub-item total WITH GST - used for minimum value validation
  const calculateSubItemTotalWithGST = () => {
    let total = 0;
    Object.entries(selectedSubItems).forEach(([subItemId, isSelected]) => {
      if (isSelected && selectedPackage) {
        const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
        if (subItem) {
          const quantity = subItemQuantities[subItemId] || 1;
          const gstInfo = calculateSubItemGST(subItem);
          // Include GST in total calculation for minimum value check
          total += gstInfo.totalWithGST * quantity;
        }
      }
    });
    return total;
  };

  // Check if minimum value is met - comparing total WITH GST against package minimum
  const isMinimumValueMet = () => {
    if (!selectedPackage) return false;
    // Use total WITH GST for minimum value validation
    return calculateSubItemTotalWithGST() >= selectedPackage.totalPackageValue;
  };

  const handleSubItemToggle = (subItemId) => {
    setSelectedSubItems(prev => ({
      ...prev,
      [subItemId]: !prev[subItemId]
    }));
    
    // Set default quantity to minimum required when selecting
    if (!selectedSubItems[subItemId]) {
      const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
      const minQty = subItem?.minQuantity || 1;
      setSubItemQuantities(prev => ({
        ...prev,
        [subItemId]: minQty
      }));
    }
  };

  const handleQuantityChange = (subItemId, quantity) => {
    const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
    const minQty = subItem?.minQuantity || 1;
    const maxQty = subItem?.maxQuantity;
    
    let numQuantity = parseInt(quantity) || minQty;
    
    // Enforce minimum
    numQuantity = Math.max(minQty, numQuantity);
    
    // Enforce maximum if set
    if (maxQty && numQuantity > maxQty) {
      numQuantity = maxQty;
      setSnackbar({
        open: true,
        message: `You can select a maximum of ${maxQty} of ${subItem.itemName}`,
      });
    }
    
    setSubItemQuantities(prev => ({
      ...prev,
      [subItemId]: numQuantity
    }));
  };

  const handleOpenSubItemSelection = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedSubItems({});
    setSubItemQuantities({});
    setSubItemSelectionOpen(true);
  };

  const handleCloseSubItemSelection = () => {
    setSubItemSelectionOpen(false);
    setSelectedPackage(null);
    setSelectedSubItems({});
    setSubItemQuantities({});
  };

  const handleOpenSubItemDetails = (pkg) => {
    setPreviewPackage(pkg);
    setSubItemDetailsOpen(true);
  };

  const handleCloseSubItemDetails = () => {
    setSubItemDetailsOpen(false);
    setPreviewPackage(null);
  };

  const handlePurchaseFromPreview = () => {
    if (previewPackage) {
      handleCloseSubItemDetails();
      handlePackagePurchase(previewPackage);
    }
  };


  const handleProceedToCheckout = () => {
    if (!isMinimumValueMet()) {
      setSnackbar({
        open: true,
        message: "Please select sub-items totaling at least " + formatPrice(selectedPackage.totalPackageValue) + " (including GST)",
      });
      return;
    }

    // Validate individual sub-item quantity limits
    for (const [subItemId, isSelected] of Object.entries(selectedSubItems)) {
      if (isSelected) {
        const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
        const quantity = subItemQuantities[subItemId] || 1;
        const minQty = subItem?.minQuantity || 1;
        const maxQty = subItem?.maxQuantity;

        if (quantity < minQty) {
          setSnackbar({
            open: true,
            message: `You must choose at least ${minQty} of "${subItem.itemName}"`,
          });
          return;
        }

        if (maxQty && quantity > maxQty) {
          setSnackbar({
            open: true,
            message: `You can select a maximum of ${maxQty} of "${subItem.itemName}"`,
          });
          return;
        }
      }
    }

    // Create a modified package object with selected sub-items and calculated total
    const selectedSubItemsList = [];
    let totalGSTAmount = 0; // Track total GST for all selected items
    
    Object.entries(selectedSubItems).forEach(([subItemId, isSelected]) => {
      if (isSelected) {
        const subItem = selectedPackage.subItems.find(item => item.id.toString() === subItemId);
        if (subItem) {
          const quantity = subItemQuantities[subItemId] || 1;
          const gstInfo = calculateSubItemGST(subItem);
          totalGSTAmount += gstInfo.gstAmount * quantity;
          
          selectedSubItemsList.push({
            ...subItem,
            selectedQuantity: quantity,
            totalPrice: subItem.itemPrice * quantity,
            gstAmount: gstInfo.gstAmount * quantity,
            totalWithGST: gstInfo.totalWithGST * quantity
          });
        }
      }
    });

    const metaWhaleFee = selectedPackage.hasMetaWhaleAccount ? (selectedPackage.metaWhaleAccountFee || 0) : 0;
    const subItemTotalWithoutGST = calculateSubItemTotal();
    const subItemTotalWithGST = calculateSubItemTotalWithGST();
    
    const packageWithSelection = {
      ...selectedPackage,
      selectedSubItems: selectedSubItems, // Keep the original object format {subItemId: boolean}
      subItemQuantities: subItemQuantities, // Keep the original object format {subItemId: quantity}
      selectedSubItemsList: selectedSubItemsList, // Also include the array format for display
      subItemTotal: subItemTotalWithoutGST, // Items total without GST
      subItemTotalWithGST: subItemTotalWithGST, // Items total WITH GST (what user saw)
      includedGSTAmount: totalGSTAmount, // GST already included in the prices user saw
      finalTotal:
        subItemTotalWithGST + CURRENT_ACTIVATION_FEE + metaWhaleFee,
      activationFee: CURRENT_ACTIVATION_FEE,
      isSubItemPackage: true,
      metaWhaleFeeIncluded: metaWhaleFee,
      gstAlreadyIncluded: true // Flag to indicate GST is already in the price
    };

    handleCloseSubItemSelection();
    onPackageSelect(packageWithSelection);
  };

  const handleAutoFillUpgradePurchase = () => {
    if (!showAutoFillUpgrade) {
      setSnackbar({
        open: true,
        message: "This upgrade is not available for your account.",
      });
      return;
    }
    onPackageSelect(buildAutoFillUpgradePackage());
  };

  const handlePackagePurchase = (pkg) => {
    if (pkg?.isAutoFillUpgrade) {
      handleAutoFillUpgradePurchase();
      return;
    }

    // ✅ PREVENT PURCHASE IF USER ALREADY HAS A PACKAGE
    if (hasPackage) {
      setSnackbar({
        open: true,
        message:
          "🎉 You already have Ultra Customer status! No need to purchase another package.",
      });
      return;
    }

    // Check if this is a sub-items package
    if (pkg.hasSubItems && pkg.subItems && pkg.subItems.length > 0) {
      handleOpenSubItemSelection(pkg);
    } else {
      // Regular package - proceed normally
      onPackageSelect(pkg);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* NavBar Component */}
        <NavBar
          user={user}
          handleMenu={handleMenu}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          categories={categories}
          cartItemCount={getCartItemCount()}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          setCartDrawerOpen={setCartDrawerOpen}
          showSearch={false} // Hide search in package store
          userProfile={userProfile}
        />

        {/* FloatingCartButton Component */}
        <FloatingCartButton
          getCartItemCount={getCartItemCount}
          setCartDrawerOpen={setCartDrawerOpen}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* CartDrawer Component */}
        <CartDrawer
          cartDrawerOpen={cartDrawerOpen}
          handleCartDrawerClose={handleCartDrawerClose}
          cart={cart}
          getCartItemCount={getCartItemCount}
          getCartTotal={getCartTotal}
          removeFromCart={removeFromCart}
          updateCartQuantity={updateCartQuantity}
          canAddMoreToCart={canAddMoreToCart}
          formatPrice={formatPrice}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          cardGlassStyles={cardGlassStyles}
          setShowCart={() => {}} // Package store doesn't have cart page
          setCartDrawerOpen={setCartDrawerOpen}
        />

        {/* Notifications Component */}
        <Notifications
          snackbar={snackbar}
          handleSnackbarClose={handleSnackbarClose}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* UserMenu Component */}
        <UserMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          isAdmin={isAdmin}
          onAdminPanel={onAdminPanel}
          handleSignOut={handleSignOut}
          setShowMyOrders={() => {}}
          setShowPackageStore={() => {}}
          setCartDrawerOpen={setCartDrawerOpen}
          setShowProfileSettings={() => {}}
          getCartItemCount={getCartItemCount}
          setDeleteDialogOpen={() => {}}
          deleteDialogOpen={false}
          handleDeleteAccount={() => {}}
          loading={false}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header with Back Button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              ...glassStyles,
              p: { xs: 2, sm: 3 },
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: primaryBlue[500],
                color: "white",
                "&:hover": {
                  bgcolor: primaryBlue[600],
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Subscription
            </Typography>
          </Box>

          {/* ✅ USER STATUS ALERT - SHOWS DIFFERENT CONTENT BASED ON PACKAGE STATUS */}
          {hasPackage ? (
            <Alert
              severity="success"
              icon={<EmojiEvents />}
              sx={{
                mb: 4,
                ...glassStyles,
                bgcolor: "rgba(76, 175, 80, 0.9)",
                color: "white",
                fontWeight: 600,
                fontSize: "1.1rem",
                "& .MuiAlert-icon": {
                  color: "white",
                  fontSize: "2rem",
                },
              }}
            >
              🎉 <strong>Congratulations!</strong> You already have{" "}
              <strong>Ultra Customer Status</strong>!
              <br />
              ✨ You can now purchase items with exclusive discounts and earn
              cashback benefits.
              {showAutoFillUpgrade ? (
                <>
                  <br />
                  📦 Your membership was activated at ₹{LEGACY_ACTIVATION_FEE}.
                  Pay <strong>₹{AUTO_FILL_UPGRADE_AMOUNT}</strong>{" "}
                  to upgrade to the current ₹{CURRENT_ACTIVATION_FEE} plan and
                  unlock the <strong>Auto Filling Program</strong>.
                </>
              ) : (
                <>
                  <br />
                  💎 No need to purchase another package - you're all set!
                </>
              )}
            </Alert>
          ) : (
            <Alert
              severity="info"
              icon={<Info />}
              sx={{
                mb: 4,
                ...glassStyles,
                bgcolor: "rgba(0, 114, 206, 0.9)",
                color: "white",
                fontWeight: 600,
                "& .MuiAlert-icon": {
                  color: "white",
                },
              }}
            >
              🎁 Choose a package below to unlock Ultra Customer benefits and
              start earning cashback!
            </Alert>
          )}

          {hasPackage && showAutoFillUpgrade && (
            <Box
              sx={{
                ...glassStyles,
                p: { xs: 3, sm: 4 },
                mb: 4,
                background:
                  "linear-gradient(135deg, rgba(0, 114, 206, 0.12), rgba(255, 193, 7, 0.08))",
                border: `2px solid ${primaryBlue[300]}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: primaryBlue[800],
                  mb: 1,
                  textAlign: "center",
                }}
              >
                Auto Filling Program — membership upgrade
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: primaryBlue[700],
                  textAlign: "center",
                  maxWidth: 640,
                  mx: "auto",
                  mb: 3,
                }}
              >
                You purchased when activation was ₹{LEGACY_ACTIVATION_FEE}. The
                current membership activation fee is ₹{CURRENT_ACTIVATION_FEE}.
                Pay the Auto Filling Program add-on fee of{" "}
                <strong>₹{AUTO_FILL_UPGRADE_AMOUNT}</strong>{" "}
                to add the Auto Filling Program to your account. After
                successful payment,{" "}
                <strong>₹{AUTO_FILL_UPGRADE_AMOUNT}</strong>{" "}
                is credited to your cashback wallet and program access is
                enabled right away.
              </Typography>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAutoFillUpgradePurchase}
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontWeight: 700,
                    background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                  }}
                >
                  Pay ₹{AUTO_FILL_UPGRADE_AMOUNT} for Auto
                  Filling Program
                </Button>
              </Box>
            </Box>
          )}

          {!hasPackage && user && (
            <MembershipVoucherRedeem
              user={user}
              userProfile={userProfile}
              primaryBlue={primaryBlue}
              onRedeemed={async () => {
                if (onRefreshProfile) {
                  await onRefreshProfile();
                }
                setSnackbar({
                  open: true,
                  message: "Ultra membership activated! Refreshing your profile…",
                });
              }}
            />
          )}

          {/* ✅ CONDITIONAL RENDERING - DIFFERENT CONTENT FOR EXISTING CUSTOMERS */}
          {hasPackage ? (
            // ✅ CONTENT FOR USERS WHO ALREADY HAVE PACKAGES
            <Box>
              {/* Ultra Customer Benefits Section */}
              <Box
                sx={{
                  ...glassStyles,
                  p: { xs: 4, sm: 6 },
                  textAlign: "center",
                  background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(129, 199, 132, 0.05))`,
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                  mb: 6,
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: `linear-gradient(45deg, #00BFFF, #60A5FA)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                    boxShadow: "0 8px 32px rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 60, color: "white" }} />
                </Box>

                <Typography
                  variant={{ xs: "h4", sm: "h3" }}
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    background: `linear-gradient(45deg, #2e7d32, #00BFFF)`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  🏆 Ultra Customer Status
                </Typography>

                <Typography
                  variant={{ xs: "h6", sm: "h5" }}
                  sx={{
                    color: "#2e7d32",
                    mb: 3,
                    fontWeight: 500,
                  }}
                >
                  You're already a premium member!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#388e3c",
                    maxWidth: "600px",
                    mx: "auto",
                    mb: 4,
                    fontSize: "1.1rem",
                  }}
                >
                  Enjoy exclusive benefits, discounts, and cashback on all your
                  purchases. Your Ultra Customer status gives you access to
                  the best deals and commissions.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  onClick={onBack}
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                    boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                    "&:hover": {
                      background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                      transform: "translateY(-2px)",
                      boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                    },
                  }}
                >
                  🛍️ Start Shopping Items
                </Button>
              </Box>

              {/* Benefits Grid for Existing Customers */}
              <Grid container spacing={4} sx={{ mb: 6 }}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      ...cardGlassStyles,
                      p: 4,
                      height: "100%",
                      background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(129, 199, 132, 0.05))`,
                      border: "1px solid rgba(76, 175, 80, 0.2)",
                    }}
                  >
                    <Typography variant="h2" sx={{ mb: 2 }}>
                      💰
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600, mb: 2 }}
                    >
                      Cashback Rewards
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#388e3c" }}>
                      Earn cashback on every purchase and grow your savings
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      ...cardGlassStyles,
                      p: 4,
                      height: "100%",
                      background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(129, 199, 132, 0.05))`,
                      border: "1px solid rgba(76, 175, 80, 0.2)",
                    }}
                  >
                    <Typography variant="h2" sx={{ mb: 2 }}>
                      🎯
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600, mb: 2 }}
                    >
                      Exclusive Discounts
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#388e3c" }}>
                      Get special pricing and exclusive deals on all items
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      textAlign: "center",
                      ...cardGlassStyles,
                      p: 4,
                      height: "100%",
                      background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(129, 199, 132, 0.05))`,
                      border: "1px solid rgba(76, 175, 80, 0.2)",
                    }}
                  >
                    <Typography variant="h2" sx={{ mb: 2 }}>
                      💎
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600, mb: 2 }}
                    >
                      Referral Points
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#388e3c" }}>
                      Earn commissions by referring friends and family
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // ✅ ORIGINAL CONTENT FOR USERS WHO DON'T HAVE PACKAGES
            <Box>
              {/* Hero Section */}
              <Box
                sx={{
                  textAlign: "center",
                  mb: 6,
                  ...glassStyles,
                  p: { xs: 4, sm: 6 },
                }}
              >
                <Typography
                  variant={{ xs: "h4", sm: "h3" }}
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  🎁 Exclusive Packages
                </Typography>
                <Typography
                  variant={{ xs: "h6", sm: "h5" }}
                  sx={{
                    color: primaryBlue[700],
                    mb: 3,
                    fontWeight: 500,
                  }}
                >
                  Choose from our premium packages designed for your success
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: primaryBlue[600],
                    maxWidth: "600px",
                    mx: "auto",
                  }}
                >
                  Unlock exclusive features, get premium support, and accelerate
                  your journey with our carefully crafted packages.
                </Typography>
              </Box>

              {/* Packages Section */}
              <Box sx={{ mb: 6 }}>
                {packagesLoading ? (
                  <Grid container spacing={4}>
                    {[1, 2, 3].map((item) => (
                      <Grid item xs={12} md={6} lg={4} key={item}>
                        <Box sx={{ ...cardGlassStyles, p: 3 }}>
                          <Skeleton variant="text" width="60%" height={40} />
                          <Skeleton
                            variant="text"
                            width="40%"
                            height={30}
                            sx={{ mb: 2 }}
                          />
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={120}
                            sx={{ mb: 2 }}
                          />
                          <Skeleton variant="text" width="80%" height={20} />
                          <Skeleton variant="text" width="90%" height={20} />
                          <Skeleton
                            variant="rectangular"
                            width="100%"
                            height={50}
                            sx={{ mt: 2 }}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ) : storePackages.length === 0 ? (
                  <Box
                    sx={{
                      ...glassStyles,
                      textAlign: "center",
                      py: 8,
                      px: 4,
                    }}
                  >
                    <ShoppingCart
                      sx={{
                        fontSize: 120,
                        mb: 3,
                        color: primaryBlue[300],
                        opacity: 0.7,
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 2,
                        fontWeight: 600,
                        color: primaryBlue[700],
                      }}
                    >
                      No Packages Available
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 4,
                        color: primaryBlue[600],
                        opacity: 0.8,
                      }}
                    >
                      Check back later for exciting packages and offers!
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={onBack}
                      sx={{
                        px: 4,
                        py: 1.5,
                        background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                        boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                        "&:hover": {
                          background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                          transform: "translateY(-2px)",
                          boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                        },
                      }}
                    >
                      Browse Items Instead
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={4}>
                    {storePackages.map((pkg) => {
                      const badge = getPackageBadge(pkg);
                      const featureLimit = getPackageFeatureLimit(pkg);

                      return (
                        <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                          <Box
                            sx={{
                              ...cardGlassStyles,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Premium Badge */}
                            <Box
                              sx={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                                zIndex: 1,
                              }}
                            >
                              <Chip
                                label={badge.label}
                                color={badge.color}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                }}
                              />
                            </Box>

                            <Box sx={{ p: 3, flexGrow: 1 }}>
                              {/* Package Header */}
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: primaryBlue[800],
                                    mb: 1,
                                    pr: 8, // Space for badge
                                  }}
                                >
                                  {pkg.name}
                                </Typography>
                                <Chip
                                  label={pkg.category}
                                  size="small"
                                  sx={{
                                    bgcolor: primaryBlue[100],
                                    color: primaryBlue[700],
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>

                              {/* Price Section */}
                              <Box sx={{ mb: 3, textAlign: "center" }}>
                                {pkg.hasSubItems ? (
                                  <>
                                    <Typography
                                      variant="h3"
                                      sx={{
                                        fontWeight: 700,
                                        background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        mb: 0.5,
                                      }}
                                    >
                                      {formatPrice(pkg.totalPackageValue + CURRENT_ACTIVATION_FEE + (pkg.hasMetaWhaleAccount ? (pkg.metaWhaleAccountFee || 0) : 0))}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: primaryBlue[600], mb: 1 }}
                                    >
                                      Total Package Price
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ 
                                        color: "#00BFFF", 
                                        fontWeight: 600,
                                        fontSize: "0.9rem"
                                      }}
                                    >
                                      {pkg.hasMetaWhaleAccount 
                                        ? `₹${pkg.totalPackageValue} Sub-items + ₹${CURRENT_ACTIVATION_FEE} Activation + ₹${pkg.metaWhaleAccountFee || 0} Meta Whale`
                                        : pkg.totalPackageValue === 10000 
                                          ? `₹${pkg.totalPackageValue} Sub-items + ₹${CURRENT_ACTIVATION_FEE} Activation Fee + Free Meta Whale Account Creation`
                                          : `₹${pkg.totalPackageValue} Sub-items + ₹${CURRENT_ACTIVATION_FEE} Activation Fee`
                                      }
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography
                                      variant="h3"
                                      sx={{
                                        fontWeight: 700,
                                        background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                                        WebkitBackgroundClip: "text",
                                        WebkitTextFillColor: "transparent",
                                        mb: 1,
                                      }}
                                    >
                                      {formatPrice(pkg.price)}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: primaryBlue[600] }}
                                    >
                                      One-time purchase
                                    </Typography>
                                  </>
                                )}
                              </Box>

                              {/* Description */}
                              <Typography
                                variant="body1"
                                sx={{
                                  color: primaryBlue[700],
                                  mb: 3,
                                  lineHeight: 1.6,
                                }}
                              >
                                {pkg.description}
                              </Typography>

                              {/* Sub-items or Features */}
                              {pkg.hasSubItems && pkg.subItems && pkg.subItems.length > 0 ? (
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: primaryBlue[800],
                                      fontWeight: 600,
                                      mb: 2,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <ShoppingCart
                                      sx={{ mr: 1, color: primaryBlue[500] }}
                                    />
                                    Contains {pkg.subItems.length} Sub-items
                                  </Typography>

                                  {/* Enhanced Sub-items Preview */}
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#2e7d32",
                                        fontWeight: 600,
                                        bgcolor: "rgba(46, 125, 50, 0.1)",
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: "1px solid rgba(46, 125, 50, 0.3)",
                                        textAlign: "center",
                                        mb: 2,
                                      }}
                                    >
                                      ✨ Flexible Package - Choose from {pkg.subItems.length} different items
                                    </Typography>

                                    {/* Show first 3 sub-items with images */}
                                    {pkg.subItems.slice(0, 3).map((subItem, index) => (
                                      <Box
                                        key={subItem.id || index}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mb: 1.5,
                                          p: 2,
                                          bgcolor: "rgba(0, 114, 206, 0.05)",
                                          borderRadius: 1,
                                          border: `1px solid ${primaryBlue[200]}`,
                                        }}
                                      >
                                        {/* Item Image */}
                                        {subItem.imageUrl && (
                                          <img
                                            src={subItem.imageUrl}
                                            alt={subItem.itemName}
                                            style={{
                                              width: 40,
                                              height: 40,
                                              objectFit: 'cover',
                                              borderRadius: '6px',
                                              marginRight: 12,
                                              border: `1px solid ${primaryBlue[200]}`,
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        )}
                                        
                                        <Box sx={{ flexGrow: 1 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: primaryBlue[800],
                                              fontWeight: 600,
                                              mb: 0.5,
                                            }}
                                          >
                                            {subItem.itemName}
                                          </Typography>
                                          {subItem.category && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: primaryBlue[600],
                                                display: 'block',
                                              }}
                                            >
                                              {subItem.category}
                                            </Typography>
                                          )}
                                        </Box>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            color: primaryBlue[700],
                                            fontWeight: 700,
                                          }}
                                        >
                                          {formatPrice(subItem.itemPrice)}
                                        </Typography>
                                      </Box>
                                    ))}

                                    {/* Show "View All" if more than 3 items */}
                                    {pkg.subItems.length > 3 && (
                                      <Box
                                        sx={{
                                          textAlign: 'center',
                                          p: 1.5,
                                          bgcolor: "rgba(0, 114, 206, 0.05)",
                                          borderRadius: 1,
                                          border: `1px solid ${primaryBlue[200]}`,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: primaryBlue[600],
                                            fontWeight: 600,
                                          }}
                                        >
                                          + {pkg.subItems.length - 3} more items available
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* View Details Button */}
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    onClick={() => handleOpenSubItemDetails(pkg)}
                                    sx={{
                                      mb: 2,
                                      borderColor: primaryBlue[300],
                                      color: primaryBlue[600],
                                      '&:hover': {
                                        borderColor: primaryBlue[500],
                                        bgcolor: 'rgba(0, 114, 206, 0.05)',
                                      },
                                    }}
                                  >
                                    🔍 View All Sub-item Details
                                  </Button>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#d32f2f",
                                      fontWeight: 600,
                                      bgcolor: "rgba(211, 47, 47, 0.1)",
                                      p: 1.5,
                                      borderRadius: 1,
                                      border: "1px solid rgba(211, 47, 47, 0.3)",
                                      textAlign: "center",
                                    }}
                                  >
                                    ⚠️ Minimum selection: {formatPrice(pkg.totalPackageValue)}
                                  </Typography>
                                </Box>
                              ) : pkg.features && pkg.features.length > 0 ? (
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: primaryBlue[800],
                                      fontWeight: 600,
                                      mb: 2,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Star
                                      sx={{ mr: 1, color: primaryBlue[500] }}
                                    />
                                    Package Features
                                  </Typography>
                                  <Box>
                                    {pkg.features
                                      .slice(0, featureLimit)
                                      .map((feature, index) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            mb: 1,
                                          }}
                                        >
                                          <CheckCircle
                                            sx={{
                                              color: "#00BFFF",
                                              fontSize: 18,
                                              mr: 1.5,
                                              mt: 0.2,
                                              flexShrink: 0,
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              color: primaryBlue[700],
                                              lineHeight: 1.4,
                                            }}
                                          >
                                            {feature}
                                          </Typography>
                                        </Box>
                                      ))}
                                    {pkg.features.length > featureLimit && (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: primaryBlue[500],
                                          fontWeight: 600,
                                          mt: 1,
                                          fontStyle: "italic",
                                        }}
                                      >
                                        +{pkg.features.length - featureLimit}{" "}
                                        more premium features
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ) : null}
                            </Box>

                            {/* Action Button */}
                            <Box sx={{ p: 3, pt: 0 }}>
                              <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={() => handlePackagePurchase(pkg)}
                                sx={{
                                  py: 1.5,
                                  fontSize: "1.1rem",
                                  fontWeight: 600,
                                  background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                                  boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                                  "&:hover": {
                                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                                    transform: "translateY(-2px)",
                                    boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                                  },
                                  transition:
                                    "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                🛒 Get This Package
                              </Button>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>

              {/* Why Choose Our Packages Section */}
              <Box
                sx={{
                  ...glassStyles,
                  p: { xs: 4, sm: 6 },
                  mt: 8,
                  background: `linear-gradient(135deg, rgba(0, 114, 206, 0.1), rgba(100, 181, 246, 0.05))`,
                  border: `1px solid ${primaryBlue[200]}`,
                }}
              >
                <Typography
                  variant={{ xs: "h5", sm: "h4" }}
                  sx={{
                    textAlign: "center",
                    fontWeight: 700,
                    mb: 4,
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  🌟 Why Choose Our Packages?
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        ...cardGlassStyles,
                        p: 3,
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h4">🎯</Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryBlue[800],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Premium Support
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: primaryBlue[700] }}
                      >
                        Get dedicated support and guidance from our expert team
                        to maximize your success
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        ...cardGlassStyles,
                        p: 3,
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h4">⚡</Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryBlue[800],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Exclusive Features
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: primaryBlue[700] }}
                      >
                        Access advanced tools and features that are exclusively
                        available to package holders
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        textAlign: "center",
                        ...cardGlassStyles,
                        p: 3,
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h4">💎</Typography>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: primaryBlue[800],
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Best Value
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: primaryBlue[700] }}
                      >
                        Carefully crafted packages designed to give you maximum
                        value and return on investment
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Special Offer Section */}
              <Box
                sx={{
                  ...glassStyles,
                  p: 4,
                  mt: 6,
                  textAlign: "center",
                  background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(129, 199, 132, 0.05))`,
                  border: "1px solid rgba(76, 175, 80, 0.3)",
                }}
              >
                <LocalOffer sx={{ fontSize: 60, color: "#00BFFF", mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "#2e7d32",
                    mb: 2,
                  }}
                >
                  🎉 First-Time Package Buyer Bonus!
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#388e3c",
                    mb: 1,
                  }}
                >
                  Get ₹{CURRENT_ACTIVATION_FEE} Cashback + Points Eligibility
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#00BFFF",
                    maxWidth: "600px",
                    mx: "auto",
                  }}
                >
                  Purchase your first package and receive instant cashback plus
                  become eligible to earn referral points. Start your
                  journey today!
                </Typography>
              </Box>
            </Box>
          )}
        </Container>

        {/* Footer Component */}
        <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />

        {/* Sub-Item Details Preview Modal */}
        <Dialog
          open={subItemDetailsOpen}
          onClose={handleCloseSubItemDetails}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              ...glassStyles,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              maxHeight: "90vh",
            },
          }}
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Info sx={{ mr: 2, color: primaryBlue[700] }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: primaryBlue[800], fontWeight: 700 }}
                  >
                    Package Sub-items Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                    {previewPackage?.name} - Complete Details
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseSubItemDetails}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {previewPackage && (
              <>
                {/* Package Overview */}
                <Alert
                  severity="info"
                  sx={{ 
                    ...glassStyles, 
                    bgcolor: "rgba(0, 114, 206, 0.1)",
                    mb: 3 
                  }}
                >
                  <Typography variant="body1">
                    <strong>Package:</strong> {previewPackage.name}
                    <br />
                    <strong>Description:</strong> {previewPackage.description}
                    <br />
                    <strong>Total Value:</strong> {formatPrice(previewPackage.totalPackageValue)} + ₹{CURRENT_ACTIVATION_FEE} Activation Fee
                    <br />
                    <strong>Available Items:</strong> {previewPackage.subItems.length} different items to choose from
                  </Typography>
                </Alert>

                <Typography variant="h6" sx={{ color: primaryBlue[800], mb: 3, fontWeight: 600 }}>
                  📦 Available Sub-items ({previewPackage.subItems.length} items)
                </Typography>

                {/* Sub-items Grid */}
                <Grid container spacing={3}>
                  {previewPackage.subItems.map((subItem) => (
                    <Grid item xs={12} md={6} key={subItem.id}>
                      <Card
                        sx={{
                          ...cardGlassStyles,
                          height: '100%',
                          border: `1px solid ${primaryBlue[200]}`,
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          {/* Item Header with Image */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            {subItem.imageUrl && (
                              <img
                                src={subItem.imageUrl}
                                alt={subItem.itemName}
                                style={{
                                  width: 80,
                                  height: 80,
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  marginRight: 16,
                                  border: `1px solid ${primaryBlue[200]}`,
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: primaryBlue[800],
                                  fontWeight: 700,
                                  mb: 1,
                                }}
                              >
                                {subItem.itemName}
                              </Typography>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: primaryBlue[600],
                                    fontWeight: 600,
                                    mb: 0.5,
                                  }}
                                >
                                  {formatPrice(subItem.sellingPrice > 0 ? subItem.sellingPrice : (subItem.basePrice + subItem.profitMargin))}
                                </Typography>
                                
                                {(() => {
                                  const discount = calculateSubItemDiscount(subItem);
                                  if (discount) {
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            textDecoration: 'line-through',
                                            color: 'text.secondary',
                                          }}
                                        >
                                          {formatPrice(discount.originalPrice)}
                                        </Typography>
                                        <Chip
                                          label={`${discount.savingPercentage.toFixed(0)}% OFF`}
                                          size="small"
                                          color="success"
                                          sx={{
                                            fontSize: '0.6rem',
                                            height: '18px',
                                            fontWeight: 600,
                                          }}
                                        />
                                      </Box>
                                    );
                                  }
                                  return null;
                                })()}

                                {(() => {
                                  const gstInfo = calculateSubItemGST(subItem);
                                  if (gstInfo.gstAmount > 0) {
                                    return (
                                      <Box sx={{ mt: 1 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            fontSize: '0.75rem',
                                          }}
                                        >
                                          + {formatPrice(gstInfo.gstAmount)} GST ({gstInfo.gstPercentage}%)
                                        </Typography>
                                        <br />
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: primaryBlue[600],
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                          }}
                                        >
                                          Total: {formatPrice(gstInfo.totalWithGST)}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  return null;
                                })()}
                              </Box>
                              {subItem.category && (
                                <Chip
                                  label={subItem.category}
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    bgcolor: primaryBlue[100],
                                    color: primaryBlue[700],
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Item Details */}
                          {subItem.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: primaryBlue[700],
                                mb: 2,
                                lineHeight: 1.5,
                              }}
                            >
                              {subItem.description}
                            </Typography>
                          )}

                          {/* Item Specifications */}
                          <Box sx={{ mb: 2 }}>
                            {subItem.brand && (
                              <Typography variant="body2" sx={{ color: primaryBlue[600], mb: 0.5 }}>
                                <strong>Brand:</strong> {subItem.brand}
                              </Typography>
                            )}
                            {subItem.weight && (
                              <Typography variant="body2" sx={{ color: primaryBlue[600], mb: 0.5 }}>
                                <strong>Weight:</strong> {subItem.weight}
                              </Typography>
                            )}
                            {subItem.dimensions && (
                              <Typography variant="body2" sx={{ color: primaryBlue[600], mb: 0.5 }}>
                                <strong>Dimensions:</strong> {subItem.dimensions}
                              </Typography>
                            )}
                            {subItem.material && (
                              <Typography variant="body2" sx={{ color: primaryBlue[600], mb: 0.5 }}>
                                <strong>Material:</strong> {subItem.material}
                              </Typography>
                            )}
                            {subItem.warranty && (
                              <Typography variant="body2" sx={{ color: primaryBlue[600], mb: 0.5 }}>
                                <strong>Warranty:</strong> {subItem.warranty}
                              </Typography>
                            )}
                          </Box>

                          {/* Colors and Sizes */}
                          {subItem.colors && subItem.colors.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ color: primaryBlue[600], fontWeight: 600 }}>
                                Available Colors:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {subItem.colors.map((color, index) => (
                                  <Chip
                                    key={index}
                                    label={color}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {subItem.sizes && subItem.sizes.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" sx={{ color: primaryBlue[600], fontWeight: 600 }}>
                                Available Sizes:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {subItem.sizes.map((size, index) => (
                                  <Chip
                                    key={index}
                                    label={size}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Quantity Info */}
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(0, 114, 206, 0.05)', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: primaryBlue[700], fontWeight: 600 }}>
                              Quantity Limits:
                            </Typography>
                            <Typography variant="body2" sx={{ color: primaryBlue[600] }}>
                              Min: {subItem.minQuantity || 1} • 
                              Max: {subItem.maxQuantity || 'No limit'}
                            </Typography>
                          </Box>


                          {/* Additional Details */}
                          {subItem.specifications && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" sx={{ color: primaryBlue[600], fontWeight: 600 }}>
                                Specifications:
                              </Typography>
                              <Typography variant="body2" sx={{ color: primaryBlue[700], mt: 0.5 }}>
                                {subItem.specifications}
                              </Typography>
                            </Box>
                          )}

                          {/* Tags and Features */}
                          {subItem.features && subItem.features.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" sx={{ color: primaryBlue[600], fontWeight: 600 }}>
                                Key Features:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {subItem.features.map((feature, index) => (
                                  <Chip
                                    key={index}
                                    label={feature}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Purchase Information */}
                <Alert
                  severity="success"
                  sx={{
                    mt: 3,
                    ...glassStyles,
                    bgcolor: "rgba(76, 175, 80, 0.1)",
                  }}
                >
                  <Typography variant="body1">
                    <strong>💡 How it works:</strong><br />
                    1. Choose items that total at least {formatPrice(previewPackage.totalPackageValue)}<br />
                    2. Pay ₹{CURRENT_ACTIVATION_FEE} activation fee (returned as cashback)<br />
                    3. Get ultra customer status and start earning benefits!
                  </Typography>
                </Alert>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleCloseSubItemDetails}
              variant="outlined"
              size="large"
            >
              Close
            </Button>
            <Button
              onClick={handlePurchaseFromPreview}
              variant="contained"
              size="large"
              disabled={hasPackage}
              sx={{
                bgcolor: hasPackage ? 'grey.400' : primaryBlue[500],
                "&:hover": { 
                  bgcolor: hasPackage ? 'grey.400' : primaryBlue[600]
                },
                px: 4,
              }}
            >
              {hasPackage ? 'Already Purchased' : '🛒 Purchase This Package'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sub-Item Selection Dialog */}
        <Dialog
          open={subItemSelectionOpen}
          onClose={handleCloseSubItemSelection}
          maxWidth="lg"
          fullWidth
          fullScreen={(theme) => theme.breakpoints.down('md')}
          PaperProps={{
            sx: {
              ...glassStyles,
              bgcolor: "rgba(255, 255, 255, 0.95)",
              maxHeight: { xs: "100vh", md: "90vh" },
              height: { xs: "100vh", md: "auto" },
              margin: { xs: 0, md: "32px" },
              borderRadius: { xs: 0, md: "16px" },
            },
          }}
        >
          <DialogTitle sx={{ pb: { xs: 1, md: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                <ShoppingCart sx={{ 
                  mr: { xs: 1, md: 2 }, 
                  color: primaryBlue[700],
                  fontSize: { xs: "1.2rem", md: "1.5rem" }
                }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant={{ xs: "subtitle1", md: "h6" }}
                    sx={{ 
                      color: primaryBlue[800], 
                      fontWeight: 700,
                      fontSize: { xs: "1rem", md: "1.25rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    Select Package Items
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: primaryBlue[600],
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedPackage?.name}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={handleCloseSubItemSelection}
                sx={{ 
                  p: { xs: 1, md: 1.5 },
                  minWidth: "auto",
                }}
              >
                <Close sx={{ fontSize: { xs: "1.2rem", md: "1.5rem" } }} />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ 
            p: { xs: 2, md: 3 }, 
            pt: { xs: 1, md: 2 },
            pb: { xs: 1, md: 2 },
            height: { xs: "calc(100vh - 120px)", md: "auto" },
            overflow: "auto",
          }}>
            {selectedPackage && (
              <>
                {/* Package Info */}
                <Alert
                  severity="info"
                  sx={{ 
                    ...glassStyles, 
                    bgcolor: "rgba(0, 114, 206, 0.1)",
                    mb: { xs: 2, md: 3 },
                    p: { xs: 1.5, md: 2 },
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: { xs: "0.8rem", md: "0.875rem" },
                      lineHeight: 1.4,
                    }}
                  >
                    <strong>Min. Value:</strong> {formatPrice(selectedPackage.totalPackageValue)}
                    <br />
                    <strong>Activation:</strong> ₹{CURRENT_ACTIVATION_FEE} (cashback)
                    <br />
                    {selectedPackage.hasMetaWhaleAccount && (
                      <>
                        <strong>Meta Whale:</strong> ₹{selectedPackage.metaWhaleAccountFee || 0}
                        <br />
                      </>
                    )}
                    {selectedPackage.totalPackageValue === 10000 && !selectedPackage.hasMetaWhaleAccount && (
                      <>
                        <strong>Meta Whale:</strong> Free
                        <br />
                      </>
                    )}
                  </Typography>
                </Alert>

                {/* Current Selection Summary */}
                <Box sx={{ 
                  mb: { xs: 2, md: 3 }, 
                  p: { xs: 1.5, md: 2 }, 
                  bgcolor: "rgba(0, 114, 206, 0.05)", 
                  borderRadius: 1 
                }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: primaryBlue[800], 
                      mb: 1,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                      fontWeight: 600,
                    }}
                  >
                    Cart ({Object.values(selectedSubItems).filter(Boolean).length} items)
                  </Typography>
                  
                  {/* Mobile-optimized summary */}
                  <Box sx={{ 
                    display: "grid", 
                    gridTemplateColumns: { xs: "1fr 1fr", md: "auto auto auto" }, 
                    gap: { xs: 0.5, md: 1 },
                    mb: 1,
                  }}>
                    <Typography variant="body2" sx={{ color: primaryBlue[700], fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                      Items: <strong>{formatPrice(calculateSubItemTotal())}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: primaryBlue[700], fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                      Activation: <strong>₹{CURRENT_ACTIVATION_FEE}</strong>
                    </Typography>
                    {selectedPackage.hasMetaWhaleAccount && (
                      <Typography variant="body2" sx={{ color: primaryBlue[700], fontSize: { xs: "0.8rem", md: "0.875rem" }, gridColumn: { xs: "1 / -1", md: "auto" } }}>
                        Meta Whale: <strong>₹{selectedPackage.metaWhaleAccountFee || 0}</strong>
                      </Typography>
                    )}
                    {selectedPackage.totalPackageValue === 10000 && !selectedPackage.hasMetaWhaleAccount && (
                      <Typography variant="body2" sx={{ color: primaryBlue[700], fontSize: { xs: "0.8rem", md: "0.875rem" }, gridColumn: { xs: "1 / -1", md: "auto" } }}>
                        Meta Whale: <strong>Free</strong>
                      </Typography>
                    )}
                  </Box>
                  
                  <Divider sx={{ my: 1 }} />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: primaryBlue[800],
                      fontSize: { xs: "1rem", md: "1.1rem" },
                      fontWeight: 700,
                    }}
                  >
                    Total: <strong>{formatPrice(calculateSubItemTotal() + CURRENT_ACTIVATION_FEE + (selectedPackage.hasMetaWhaleAccount ? selectedPackage.metaWhaleAccountFee || 0 : 0))}</strong>
                  </Typography>
                  
                  {!isMinimumValueMet() && (
                    <Alert severity="warning" sx={{ mt: 1.5, p: 1 }}>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}
                      >
                        Need {formatPrice(selectedPackage.totalPackageValue - calculateSubItemTotalWithGST())} more (incl. GST) to meet minimum value.
                      </Typography>
                    </Alert>
                  )}
                </Box>

                {/* Enhanced Sub-items Selection */}
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    color: primaryBlue[800], 
                    mb: { xs: 1.5, md: 2 },
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    fontWeight: 600,
                  }}
                >
                  📦 Available Items
                </Typography>
                
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  {selectedPackage.subItems.map((subItem) => {
                    const subItemId = subItem.id.toString();
                    const isSelected = selectedSubItems[subItemId];
                    const quantity = subItemQuantities[subItemId] || 1;
                    
                    return (
                      <Grid item xs={12} sm={6} lg={4} key={subItem.id}>
                        <Card
                          sx={{
                            ...cardGlassStyles,
                            border: isSelected 
                              ? `2px solid ${primaryBlue[500]}` 
                              : `1px solid ${primaryBlue[200]}`,
                            bgcolor: isSelected 
                              ? "rgba(0, 114, 206, 0.15)" 
                              : "rgba(255, 255, 255, 0.9)",
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: { xs: 'none', md: 'translateY(-2px)' },
                              boxShadow: { xs: 'none', md: '0 8px 25px rgba(0,0,0,0.15)' },
                              borderColor: primaryBlue[400],
                            },
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <CardContent sx={{ 
                            p: { xs: 2, md: 3 }, 
                            position: 'relative',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}>
                            {/* Selection Indicator */}
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: { xs: 8, md: 12 },
                                  right: { xs: 8, md: 12 },
                                  backgroundColor: primaryBlue[500],
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: { xs: 24, md: 28 },
                                  height: { xs: 24, md: 28 },
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: { xs: '12px', md: '14px' },
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                  zIndex: 1,
                                }}
                              >
                                ✓
                              </Box>
                            )}

                            {/* Compact Product Display */}
                            <Box sx={{ 
                              display: 'flex', 
                              mb: { xs: 1, md: 1.5 },
                              pr: { xs: 3, md: 4 }, // Space for selection indicator
                            }}>
                              {/* Product Image */}
                              {subItem.imageUrl && (
                                <Box
                                  component="img"
                                  src={subItem.imageUrl}
                                  alt={subItem.itemName}
                                  sx={{
                                    width: { xs: 50, md: 70 },
                                    height: { xs: 50, md: 70 },
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    mr: { xs: 1.5, md: 2 },
                                    border: `2px solid ${isSelected ? primaryBlue[500] : primaryBlue[200]}`,
                                    flexShrink: 0,
                                  }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              
                              {/* Product Info */}
                              <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                {/* Product Name */}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 600,
                                    color: primaryBlue[800],
                                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    mb: 0.5,
                                  }}
                                >
                                  {subItem.itemName}
                                </Typography>
                                
                                {/* Price and Discount */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    variant="h6" 
                                    sx={{ 
                                      color: primaryBlue[600], 
                                      fontWeight: 700,
                                      fontSize: { xs: "1rem", md: "1.1rem" },
                                    }}
                                  >
                                    {formatPrice(subItem.sellingPrice > 0 ? subItem.sellingPrice : (subItem.basePrice + subItem.profitMargin))}
                                  </Typography>
                                  
                                  {(subItem.compareAtPrice > 0 || subItem.sellingPrice > 0) && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        textDecoration: 'line-through',
                                        color: 'text.secondary',
                                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                                      }}
                                    >
                                      {formatPrice(subItem.compareAtPrice > 0 ? subItem.compareAtPrice : subItem.sellingPrice)}
                                    </Typography>
                                  )}
                                  
                                  {/* Savings badge */}
                                  {(() => {
                                    const discount = calculateSubItemDiscount(subItem);
                                    if (discount && discount.savingAmount > 0) {
                                      return (
                                        <Chip
                                          label={`${discount.savingPercentage.toFixed(0)}% OFF`}
                                          size="small"
                                          color="success"
                                          sx={{
                                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                                            height: { xs: '16px', md: '18px' },
                                            fontWeight: 600,
                                          }}
                                        />
                                      );
                                    }
                                    return null;
                                  })()}
                                </Box>
                                
                                {/* GST Information */}
                                {(() => {
                                  const gstInfo = calculateSubItemGST(subItem);
                                  if (gstInfo.gstAmount > 0) {
                                    return (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.secondary',
                                          fontSize: { xs: "0.65rem", md: "0.7rem" },
                                          display: 'block',
                                          mt: 0.5,
                                        }}
                                      >
                                        + {formatPrice(gstInfo.gstAmount)} GST | Total: {formatPrice(gstInfo.totalWithGST)}
                                      </Typography>
                                    );
                                  }
                                  return null;
                                })()}
                              </Box>
                            </Box>
                            
                            {/* Add to Cart Section */}
                            <Box sx={{ 
                              mt: 'auto',
                              p: { xs: 1.5, md: 2 }, 
                              bgcolor: isSelected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(0, 114, 206, 0.05)', 
                              borderRadius: 1,
                              border: `1px solid ${isSelected ? 'rgba(76, 175, 50, 0.3)' : primaryBlue[200]}`
                            }}>
                              {isSelected ? (
                                // Item is in cart - show quantity controls
                                <Box>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#00BFFF', 
                                      mb: 1, 
                                      fontWeight: 600,
                                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                                      display: 'block',
                                    }}
                                  >
                                    ✅ Added to Cart
                                  </Typography>
                                  
                                  {/* Mobile-optimized quantity controls */}
                                  <Box sx={{ 
                                    display: "flex", 
                                    flexDirection: { xs: "column", sm: "row" },
                                    alignItems: { xs: "stretch", sm: "center" }, 
                                    justifyContent: "space-between",
                                    gap: { xs: 1, sm: 0 },
                                  }}>
                                    <Box sx={{ 
                                      display: "flex", 
                                      alignItems: "center", 
                                      gap: 1,
                                      justifyContent: { xs: "center", sm: "flex-start" },
                                    }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQuantityChange(subItemId, quantity - 1);
                                        }}
                                        disabled={quantity <= (subItem.minQuantity || 1)}
                                        sx={{ 
                                          bgcolor: 'white',
                                          '&:hover': { bgcolor: primaryBlue[50] },
                                          width: { xs: 36, md: 40 },
                                          height: { xs: 36, md: 40 },
                                        }}
                                      >
                                        <Remove sx={{ fontSize: { xs: "1rem", md: "1.2rem" } }} />
                                      </IconButton>
                                      <TextField
                                        size="small"
                                        value={quantity}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleQuantityChange(subItemId, e.target.value);
                                        }}
                                        type="number"
                                        inputProps={{ 
                                          min: subItem.minQuantity || 1, 
                                          max: subItem.maxQuantity || undefined,
                                          style: { 
                                            textAlign: 'center', 
                                            fontSize: '0.9rem',
                                          } 
                                        }}
                                        sx={{ 
                                          width: { xs: '70px', md: '80px' },
                                          '& .MuiInputBase-root': { 
                                            bgcolor: 'white',
                                            height: { xs: 36, md: 40 },
                                          }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQuantityChange(subItemId, quantity + 1);
                                        }}
                                        disabled={subItem.maxQuantity && quantity >= subItem.maxQuantity}
                                        sx={{ 
                                          bgcolor: 'white',
                                          '&:hover': { bgcolor: primaryBlue[50] },
                                          width: { xs: 36, md: 40 },
                                          height: { xs: 36, md: 40 },
                                        }}
                                      >
                                        <Add sx={{ fontSize: { xs: "1rem", md: "1.2rem" } }} />
                                      </IconButton>
                                    </Box>
                                    
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubItemToggle(subItemId);
                                      }}
                                      sx={{ 
                                        ml: { xs: 0, sm: 1 },
                                        fontSize: { xs: "0.75rem", md: "0.875rem" },
                                        px: { xs: 2, md: 3 },
                                        py: { xs: 0.5, md: 0.75 },
                                      }}
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                  
                                  <Typography
                                    variant="body2"
                                    sx={{ 
                                      color: '#00BFFF',
                                      fontWeight: 600,
                                      mt: 1,
                                      textAlign: 'center',
                                      fontSize: { xs: "0.85rem", md: "1rem" },
                                    }}
                                  >
                                    Subtotal: {formatPrice(calculateSubItemGST(subItem).totalWithGST * quantity)}
                                  </Typography>
                                </Box>
                              ) : (
                                // Item not in cart - show Add to Cart button
                                <Box sx={{ textAlign: 'center' }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="medium"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSubItemToggle(subItemId);
                                    }}
                                    sx={{
                                      py: { xs: 1.2, md: 1.5 },
                                      px: { xs: 2, md: 3 },
                                      fontSize: { xs: "0.85rem", md: "1rem" },
                                      fontWeight: 600,
                                      background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                                      boxShadow: `0 4px 15px ${primaryBlue[200]}`,
                                      '&:hover': {
                                        background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                                        transform: { xs: 'none', md: 'translateY(-2px)' },
                                        boxShadow: { xs: `0 4px 15px ${primaryBlue[200]}`, md: `0 6px 20px ${primaryBlue[300]}` },
                                      },
                                      transition: 'all 0.3s ease',
                                      width: '100%',
                                      minHeight: { xs: '44px', md: '48px' }, // Touch-friendly minimum
                                    }}
                                  >
                                    🛒 Add to Cart
                                  </Button>
                                  <Typography
                                    variant="caption"
                                    sx={{ 
                                      color: primaryBlue[600],
                                      mt: 0.5,
                                      fontSize: { xs: "0.7rem", md: "0.75rem" },
                                      display: 'block',
                                    }}
                                  >
                                    Min: {subItem.minQuantity || 1} • {formatPrice(calculateSubItemGST(subItem).totalWithGST)} each
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ 
            p: { xs: 2, md: 3 }, 
            gap: { xs: 1, md: 2 },
            flexDirection: { xs: "column", sm: "row" },
            position: { xs: "sticky", md: "static" },
            bottom: { xs: 0, md: "auto" },
            bgcolor: { xs: "rgba(255, 255, 255, 0.95)", md: "transparent" },
            borderTop: { xs: "1px solid rgba(0,0,0,0.1)", md: "none" },
            backdropFilter: { xs: "blur(10px)", md: "none" },
            zIndex: 1,
          }}>
            <Button
              onClick={handleCloseSubItemSelection}
              variant="outlined"
              sx={{
                order: { xs: 2, sm: 1 },
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: "48px", md: "auto" },
                fontSize: { xs: "0.9rem", md: "1rem" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedToCheckout}
              variant="contained"
              disabled={!isMinimumValueMet()}
              sx={{
                order: { xs: 1, sm: 2 },
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: "48px", md: "auto" },
                bgcolor: isMinimumValueMet() ? primaryBlue[500] : 'grey.400',
                "&:hover": { 
                  bgcolor: isMinimumValueMet() ? primaryBlue[600] : 'grey.400'
                },
                px: { xs: 2, md: 4 },
                fontSize: { xs: "0.85rem", md: "1rem" },
                fontWeight: 600,
              }}
            >
              {isMinimumValueMet() 
                ? `🚀 Checkout (${formatPrice(calculateSubItemTotalWithGST() + CURRENT_ACTIVATION_FEE + (selectedPackage?.hasMetaWhaleAccount ? selectedPackage?.metaWhaleAccountFee || 0 : 0))})` 
                : `Need ${formatPrice(Math.max(0, selectedPackage?.totalPackageValue - calculateSubItemTotalWithGST()) || 0)} More`
              }
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PackageStore;
